import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import axiosClient from "@/lib/axiosClient";
import { z } from "zod";

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().nonempty("Текущий пароль обязателен"),
    newPassword: z.string().min(8, "Минимум 8 символов"),
    confirmPassword: z.string().nonempty("Подтверждение пароля обязательно"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Пароли не совпадают",
    path: ["confirmPassword"],
  });

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

const changePassword = async ({
  currentPassword,
  newPassword,
  confirmPassword,
}: ChangePasswordFormValues) => {
  const response = await axiosClient.patch("/api/user/change-password", {
    currentPassword,
    newPassword,
    confirmPassword,
  });
  return response.data;
};

export default function ProfileSettingsForm({
  onSuccessAction,
}: {
  onSuccessAction?: () => void;
}) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
  });

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      toast({ title: "Успех", description: data.message, duration: 3000 });
      reset();
      if (onSuccessAction) {
        onSuccessAction();
      }
    },
    onError: (error: any) => {
      reset();
      toast({
        title: "Ошибка",
        description:
          error.response?.data?.message || "Не удалось изменить пароль.",
        duration: 3000,
      });
    },
  });

  const onSubmit = (data: ChangePasswordFormValues) => {
    changePasswordMutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="currentPassword">Текущий пароль</Label>
        <Input
          id="currentPassword"
          type="password"
          placeholder="Введите текущий пароль"
          {...register("currentPassword")}
        />
        {errors.currentPassword && (
          <p className="text-sm text-red-500">
            {errors.currentPassword.message}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="newPassword">Новый пароль</Label>
        <Input
          id="newPassword"
          type="password"
          placeholder="Введите новый пароль"
          {...register("newPassword")}
        />
        {errors.newPassword && (
          <p className="text-sm text-red-500">{errors.newPassword.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
        <Input
          id="confirmPassword"
          type="password"
          placeholder="Подтвердите новый пароль"
          {...register("confirmPassword")}
        />
        {errors.confirmPassword && (
          <p className="text-sm text-red-500">
            {errors.confirmPassword.message}
          </p>
        )}
      </div>
      <Button type="submit">Изменить пароль</Button>
    </form>
  );
}
